import { useState, useEffect } from 'react';
import { Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const EmbeddedGallery = ({ category = '', title = 'Photo Gallery' }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        // Use gallery endpoint which is public
        const response = await fetch(`${API_URL}/api/gallery`);
        if (response.ok) {
          let data = await response.json();
          // Filter by category if specified
          if (category && category !== 'all') {
            data = data.filter(img => 
              img.category?.toLowerCase() === category.toLowerCase()
            );
          }
          setImages(data);
        }
      } catch (err) {
        console.error('Failed to fetch gallery images:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, [category]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  if (images.length === 0) {
    return null; // Don't show anything if no images
  }

  return (
    <div className="py-8">
      {title && (
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-primary-900 mb-6 text-center">
          {title}
        </h2>
      )}
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((image, index) => (
          <div 
            key={image.id || index}
            className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
            onClick={() => setLightboxImage(image)}
          >
            <img 
              src={image.imageUrl}
              alt={image.title || image.caption || 'Gallery image'}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
            {image.title && (
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-sm font-medium truncate">{image.title}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-secondary transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={lightboxImage.imageUrl}
            alt={lightboxImage.title || 'Gallery image'}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {(lightboxImage.title || lightboxImage.caption) && (
            <div className="absolute bottom-4 left-4 right-4 text-center">
              <p className="text-white text-lg font-medium">{lightboxImage.title}</p>
              {lightboxImage.caption && (
                <p className="text-slate-300 text-sm mt-1">{lightboxImage.caption}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Function to parse content and replace shortcodes with gallery components
export const parseGalleryShortcodes = (content) => {
  if (!content) return { html: content, galleries: [] };
  
  const shortcodeRegex = /\[gallery:([^\]]+)\]/g;
  const galleries = [];
  let match;
  let index = 0;
  
  while ((match = shortcodeRegex.exec(content)) !== null) {
    galleries.push({
      id: `gallery-${index}`,
      category: match[1].trim(),
      placeholder: match[0]
    });
    index++;
  }
  
  // Replace shortcodes with placeholder divs
  let html = content;
  galleries.forEach((g, i) => {
    html = html.replace(g.placeholder, `<div data-gallery-placeholder="${i}"></div>`);
  });
  
  return { html, galleries };
};
